import { LoadingState } from '#/components/common/loading-state'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { BellRing } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { InboxList } from '#/components/inbox/inbox-list'
import { useInbox } from '#/lib/api-inbox'
import { useHydratedSession } from '#/lib/auth-client'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

/**
 * Inspirasi F1 — "Perlu Anda": one mobile-first queue of everything waiting for the user,
 * across every project, each item with its evidence and its primary action.
 */
export const Route = createFileRoute('/inbox')({ component: InboxPage })

type Filter = 'all' | 'blocked' | 'done'

function InboxPage() {
  const { data: session, isPending } = useHydratedSession()
  const { data, isLoading, isError, refetch } = useInbox(Boolean(session?.user))
  const [filter, setFilter] = useState<Filter>('all')
  const items = (data?.items ?? []).filter((i) => filter === 'all' || i.state === filter)
  const blocked = data?.counts.blocked ?? 0
  return (
    <>
      <AppHeader showStats={false} compact />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-24 sm:p-6">
          <header>
            <h1 className="flex items-center gap-2 font-heading text-xl font-semibold">
              <BellRing className="size-5 text-status-progress" /> {m.inbox_title()}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{m.inbox_subtitle()}</p>
            {data ? <p className="mt-2 text-xs font-medium" data-testid="inbox-count">
              {blocked ? m.inbox_count_blocked({ n: blocked }) : m.inbox_count_none()}
            </p> : null}
          </header>
          <div className="flex flex-wrap gap-1" role="tablist">
            {(
              [
                ['all', m.inbox_filter_all()],
                ['blocked', m.inbox_filter_blocked()],
                ['done', m.inbox_filter_done()],
              ] as [Filter, string][]
            ).map(([f, l]) => (
              <Button key={f} size="sm" variant={filter === f ? 'secondary' : 'ghost'} className={cn('h-8 text-xs')} onClick={() => setFilter(f)} role="tab" aria-selected={filter === f}>
                {l}
              </Button>
            ))}
          </div>
          {isPending || isLoading ? (
            <LoadingState compact label={m.inbox_loading()} />
          ) : !session?.user ? (
            <Button asChild><Link to="/sign-in" search={{ redirect: '/inbox' }}>{m.common_signin()}</Link></Button>
          ) : isError ? (
            <div role="alert" className="space-y-3 rounded-xl border p-6">
              <p className="text-sm text-muted-foreground">{m.common_error_generic()}</p>
              <Button variant="outline" onClick={() => void refetch()}>{m.brand_retry()}</Button>
            </div>
          ) : (
            <>
              <InboxList items={items} />
              {!items.length ? <Button asChild variant="outline"><Link to="/home">{m.brand_back_home()}</Link></Button> : null}
            </>
          )}
        </div>
      </main>
    </>
  )
}
